<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { CannedItem, CannedReply } from '../../api/types'
import { ACCEPT, channelBlock, kindOf } from '../../lib/media'
import { gapTokens } from '../../lib/script'
import { rankCanned, slashAt, withoutToken, type SlashToken } from '../../lib/slash'
import { NUMBER_MARK, gapFlash, useInbox, type ComposerState } from '../../stores/inbox'
import { CHAT_FOLDER, sendMediaOf, useLibrary } from '../../stores/library'
import { useScript } from '../../stores/script'
import MediaThumb from '../library/MediaThumb.vue'
import Icon from '../ui/Icon.vue'
import DraftBox from './DraftBox.vue'
import EmojiButton from './EmojiButton.vue'
import LibraryDrawer from './LibraryDrawer.vue'
import SlashPicker from './SlashPicker.vue'

const props = defineProps<{ convId: string }>()
const store = useInbox()
const lib = useLibrary()
const script = useScript()

const box = ref<HTMLTextAreaElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
/** Library panel (image button) open · files dragged over the composer. */
const drawer = ref(false)
const dragOver = ref(false)

const EMPTY: ComposerState = { text: '', origin: 'manual', edited: false, cannedId: null }
const st = computed<ComposerState>(() => store.composers[props.convId] ?? EMPTY)
const ai = computed(() => store.details[props.convId]?.ai ?? null)
const channel = computed(() => store.details[props.convId]?.channel ?? store.convs[props.convId]?.channel ?? null)
/** AI mode (docs/ai-mode.md): the AI is in charge — sending anything stops it at once. */
const aiInCharge = computed(() => !!store.convs[props.convId]?.ai_mode)
/** The text in the typing box came from the draft box ("ใช้ร่างนี้", maybe edited). */
const isDraft = computed(() => st.value.origin === 'ai' && st.value.text.trim() !== '')
/** 2 answers under the box: the AI's pick for this chat once a draft was made, else the team's most used. */
const canned = computed<CannedReply[]>(() => {
  const list = ai.value?.canned ?? []
  return (list.length ? list : store.cannedDefault).slice(0, 2)
})
const text = computed({
  get: () => st.value.text,
  set: (v: string) => store.setText(props.convId, v),
})

// ------------------------------------------------ staged pictures / uploads
const staged = computed(() => lib.stagedFor(props.convId))
const uploads = computed(() => lib.uploadsFor(props.convId))
const uploading = computed(() => uploads.value.some((u) => u.status !== 'done' && u.status !== 'error'))
const uploadErrors = computed(() => uploads.value.filter((u) => u.status === 'error'))
/** LINE takes 5 message objects per reply: the text + 4 pictures. */
const lineOver = computed(() =>
  channel.value === 'line' ? Math.max(0, (st.value.text.trim() ? 1 : 0) + staged.value.length - 5) : 0,
)
const blocked = computed(() =>
  staged.value
    .map((s) => ({ s, why: channelBlock(s.asset, channel.value) }))
    .filter((x): x is { s: (typeof staged.value)[number]; why: string } => !!x.why),
)
const canSend = computed(
  () => (st.value.text.trim().length > 0 || staged.value.length > 0) && !uploading.value && !lineOver.value && !blocked.value.length,
)
/** The server put this mark where the draft had a number it could not verify (docs/ai.md guardrails). */
const needsCheck = computed(() => st.value.text.includes(NUMBER_MARK))
/** A blank of the suggestion that was moved in ("[ค่าส่ง]") is still there: sending waits for it. */
const openGap = computed(() => store.openGap(props.convId))

/** The line above the typing box says what is in it: an answer from the library, or a draft the staff took. */
const cannedShortcut = computed(() => (st.value.cannedId ? lib.findCanned(st.value.cannedId, props.convId)?.shortcut ?? null : null))
const head = computed<'canned' | 'quick' | 'menu' | 'ai' | 'script' | null>(() => {
  if (st.value.origin === 'canned' && st.value.text.trim()) return 'canned'
  if (st.value.origin === 'script' && st.value.text.trim()) return 'script'
  // which draft was taken ("ใช้ร่างนี้"): Jev's quick answer, the menu list, or an AI-written draft
  if (isDraft.value) return st.value.draftKind ?? 'ai'
  return null
})
/** What the band above the typing box can show (DraftBox): a suggestion, the step card, the AI draft row. */
const bandHas = computed(
  () => store.draftPhase(props.convId) !== 'hidden' || script.showing(props.convId) || !!script.stepCard(props.convId),
)
/** "แนะนำคำตอบ" (owner 18 ต.ค. 69): the band shows only once pressed — a dot says a suggestion is waiting. */
const bandOn = computed(() => script.bandOpen === props.convId)
const suggestWaiting = computed(() => script.showing(props.convId) || !!script.stepCard(props.convId))
/** The draft box sits above the typing box (not for AI-mode chats / no AI): the box keeps a little air under it. */
const band = computed(() => bandOn.value && bandHas.value)
// another chat starts with the band closed
watch(
  () => props.convId,
  (_id, was) => {
    if (was) script.closeBand(was)
  },
)

// Files that rode in with a taken draft (quick answer pictures, the round's poster) come off when the box is cleared
watch(
  () => [props.convId, isDraft.value] as const,
  ([id, on], old) => {
    if (!on && old && old[0] === id && old[1]) {
      lib.unstageSource(id, 'quick')
      lib.unstageSource(id, 'draft')
    }
  },
)

/**
 * "ใช้ร่างนี้" (or Tab on an empty box): the draft goes into the typing box and its files are staged with it.
 * `replace` also takes off the files of the text it replaces (a library answer's); what the staff attached stays.
 */
function takeDraft(mode: 'replace' | 'append'): void {
  const a = ai.value
  if (!a?.draft) return
  const q = a.draft_source === 'canned' && a.quick ? a.quick : null
  const media = a.media ?? []
  if (mode === 'replace') for (const src of ['canned', 'quick', 'draft'] as const) lib.unstageSource(props.convId, src)
  // the draft of "ปรับตามแนวลูกค้า" / "เรียบเรียงใหม่" keeps the suggestion's blanks: they still block the send
  const sg = script.ui[props.convId]?.aiView ? script.current(props.convId) : null
  if (!store.useDraft(props.convId, mode, sg ? gapTokens(sg) : [])) return
  lib.resetQuick(props.convId)
  lib.resetDraftMedia(props.convId)
  if (q) void lib.stageQuick(props.convId, q.canned_id)
  if (media.length) lib.stageDraftMedia(props.convId, media)
  void nextTick(() => {
    autosize()
    caretEnd()
  })
}

/**
 * "ใส่ในช่องพิมพ์" of the "แนะนำให้ตอบ" card (or Tab on an empty box): the suggestion goes into the typing box —
 * never sent. `replace` also takes off the files of the text it replaces, like "ใช้ร่างนี้".
 */
function takeSuggestion(mode: 'replace' | 'append'): void {
  if (mode === 'replace') for (const src of ['canned', 'quick', 'draft'] as const) lib.unstageSource(props.convId, src)
  if (!script.insert(props.convId, mode)) return
  void nextTick(() => {
    autosize()
    caretEnd()
  })
}

// ------------------------------------------------ "/" picker
const token = ref<SlashToken | null>(null)
const active = ref(0)
const pickerLoading = ref(false)
const pickerList = computed<CannedItem[]>(() =>
  token.value ? rankCanned(lib.pickerItems[props.convId] ?? [], token.value.query) : [],
)

async function openPicker(t: SlashToken): Promise<void> {
  const wasOpen = !!token.value
  token.value = t
  if (!wasOpen) active.value = 0
  if (!wasOpen) {
    pickerLoading.value = !lib.pickerItems[props.convId]
    await lib.loadPicker(props.convId)
    pickerLoading.value = false
  }
}

function closePicker(): void {
  token.value = null
  active.value = 0
}

function syncPicker(): void {
  const el = box.value
  if (!el || el.selectionStart !== el.selectionEnd) return closePicker()
  const t = slashAt(el.value, el.selectionStart)
  if (t) void openPicker(t)
  else closePicker()
}

watch(pickerList, (l) => {
  if (active.value >= l.length) active.value = Math.max(0, l.length - 1)
})

function pickAnswer(item: CannedItem): void {
  const t = token.value
  closePicker()
  if (t) store.setText(props.convId, withoutToken(st.value.text, t))
  const at = t ? { start: t.start, end: t.start } : undefined
  store.useCanned(props.convId, { id: item.id, label: item.title, text: item.text }, at, 'slash')
  for (const m of sendMediaOf(item)) lib.stage(props.convId, m, 'canned')
  void nextTick(() => {
    autosize()
    caretAfter(item.text, t?.start ?? 0)
  })
}

// ------------------------------------------------ textarea
/** The menu list is read as a whole before sending: its box grows further than a normal reply's. */
const tallBox = computed(() => head.value === 'menu')

function autosize(): void {
  const el = box.value
  if (!el) return
  // Measure with the box collapsed, but hold the composer's height meanwhile: the momentary collapse let the thread
  // above grow and lose its place at the end (a tall draft like the menu list hid the customer's last message).
  const hold = el.parentElement
  if (hold) hold.style.minHeight = `${hold.offsetHeight}px`
  el.style.height = 'auto'
  // the menu list may grow to 360 px, but never past 40% of the window (the thread above stays readable)
  const cap = tallBox.value ? Math.max(180, Math.min(360, Math.round(window.innerHeight * 0.4))) : 180
  el.style.height = `${Math.min(el.scrollHeight, cap)}px`
  if (hold) hold.style.minHeight = ''
}

watch(
  () => [props.convId, st.value.text, tallBox.value],
  () => void nextTick(autosize),
  { immediate: true },
)
// Thai web fonts change line breaks once loaded
void document.fonts?.ready.then(() => autosize())
watch(
  () => props.convId,
  () => {
    closePicker()
    drawer.value = false
    void nextTick(() => box.value?.focus({ preventScroll: true }))
  },
  { immediate: true },
)

function caretEnd(): void {
  const el = box.value
  if (!el) return
  el.focus()
  const n = el.value.length
  el.setSelectionRange(n, n)
}

/** Caret right after an inserted answer (or at the end when it replaced everything). */
function caretAfter(inserted: string, from: number): void {
  const el = box.value
  if (!el) return
  el.focus()
  const i = el.value.indexOf(inserted, Math.max(0, from - 1))
  const n = i >= 0 ? i + inserted.length : el.value.length
  el.setSelectionRange(n, n)
}

function onKey(e: KeyboardEvent): void {
  if (e.isComposing) return
  if (token.value) {
    const n = pickerList.value.length
    if (e.key === 'ArrowDown' && n) {
      e.preventDefault()
      active.value = (active.value + 1) % n
      return
    }
    if (e.key === 'ArrowUp' && n) {
      e.preventDefault()
      active.value = (active.value - 1 + n) % n
      return
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && n) {
      e.preventDefault()
      const item = pickerList.value[active.value]
      if (item) pickAnswer(item)
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      closePicker()
      return
    }
  }
  // Tab on the empty typing box = the draft box: the suggestion on screen, else ask the AI now / take its draft
  // (suggestions switched off by this staff member: Tab is a plain Tab)
  if (e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey && st.value.text.trim() === '' && !script.suggestOff) {
    if (script.showing(props.convId)) {
      e.preventDefault()
      takeSuggestion('replace')
      return
    }
    const phase = store.draftPhase(props.convId)
    if (phase === 'collapsed' || phase === 'ready' || phase === 'generating') {
      e.preventDefault()
      if (phase === 'collapsed') {
        script.openBand(props.convId)
        void store.openDraft(props.convId)
      }
      else if (phase === 'ready') takeDraft('replace')
      return
    }
  }
  if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
    e.preventDefault()
    void send()
  }
}

function pickCanned(c: CannedReply): void {
  const el = box.value
  store.useCanned(props.convId, c, el ? { start: el.selectionStart, end: el.selectionEnd } : undefined, 'button')
  void lib.stageCanned(props.convId, c.id)
  void nextTick(caretEnd)
}

async function send(): Promise<void> {
  if (uploading.value) {
    store.flash('รอให้อัปโหลดรูปเสร็จก่อนส่ง')
    return
  }
  if (!canSend.value) return
  if (st.value.text.includes(NUMBER_MARK)) {
    store.flash('ร่างยังมี [เช็คตัวเลข] ใส่ตัวเลขที่ถูกต้องก่อนส่ง')
    return
  }
  if (openGap.value) {
    store.flash(gapFlash(openGap.value))
    return
  }
  closePicker()
  const { assets, restore } = lib.takeStaged(props.convId)
  await store.send(props.convId, { media: assets, onRefused: restore })
  script.closeBand(props.convId)   // sent: the band closes until "แนะนำคำตอบ" again
  void nextTick(() => box.value?.focus({ preventScroll: true }))
}

// ------------------------------------------------ files: button, drag & drop, paste

function upload(files: File[]): void {
  const ok = files.filter((f) => kindOf(f.type))
  if (ok.length < files.length) store.flash('แนบได้เฉพาะรูป (jpg, png, webp) และวิดีโอ (mp4)')
  if (!ok.length) return
  if (!lib.uploadsEnabled) {
    store.flash('ยังไม่ได้ตั้งค่าที่เก็บไฟล์ แจ้งผู้ดูแลระบบ')
    return
  }
  void lib.uploadFiles(ok, { folder: CHAT_FOLDER, convId: props.convId })
}

function onFiles(e: Event): void {
  const input = e.target as HTMLInputElement
  upload([...(input.files ?? [])])
  input.value = ''
}

function hasFiles(e: DragEvent): boolean {
  return [...(e.dataTransfer?.types ?? [])].includes('Files')
}

function onDragOver(e: DragEvent): void {
  if (!hasFiles(e)) return
  e.preventDefault()
  dragOver.value = true
}

function onDrop(e: DragEvent): void {
  if (!hasFiles(e)) return
  e.preventDefault()
  dragOver.value = false
  upload([...(e.dataTransfer?.files ?? [])])
}

function onPaste(e: ClipboardEvent): void {
  const files = [...(e.clipboardData?.files ?? [])].filter((f) => kindOf(f.type))
  if (!files.length) return
  e.preventDefault()
  upload(files)
}

function openDrawer(): void {
  closePicker()
  drawer.value = !drawer.value
  if (drawer.value) void lib.ensureMedia()
}

function pct(p: number): string {
  return `${Math.min(99, Math.round(p * 100))}%`
}
</script>

<template>
  <div class="composer" @dragover="onDragOver" @dragleave.self="dragOver = false" @drop="onDrop">
    <SlashPicker
      v-if="token"
      :items="pickerList"
      :active="active"
      :query="token.query"
      :loading="pickerLoading"
      :can-manage="lib.canManageCanned"
      @hover="active = $event"
      @pick="pickAnswer"
    />
    <LibraryDrawer v-if="drawer" :conv-id="convId" :channel="channel" @close="drawer = false" @files="upload" />
    <p v-if="aiInCharge" class="ai-note" role="note">
      <Icon name="sparkles" /><span><b>AI กำลังดูแลแชทนี้</b> · ถ้าคุณพิมพ์ส่ง AI จะหยุดทันที</span>
    </p>
    <div class="cmp" :class="{ drop: dragOver }">
      <DraftBox :conv-id="convId" @use="takeDraft" @insert="takeSuggestion" />
      <div v-if="head" class="cmp-head">
        <span v-if="head === 'canned'" class="ai-chip quick"
          ><Icon name="zap" />คำตอบสำเร็จรูป<span v-if="cannedShortcut" class="mono">/{{ cannedShortcut }}</span></span
        >
        <span v-else-if="head === 'quick'" class="ai-chip quick"
          ><Icon name="zap" />คำตอบสำเร็จรูป<span v-if="cannedShortcut" class="mono">/{{ cannedShortcut }}</span></span
        >
        <span v-else-if="head === 'menu'" class="ai-chip quick menu"><Icon name="utensils" />เมนูรอบนี้ (อนุมัติแล้ว)</span>
        <span v-else-if="head === 'script'" class="ai-chip"><Icon name="sparkles" />แนะนำให้ตอบ</span>
        <span v-else class="ai-chip"><Icon name="sparkles" />ร่างจาก AI</span>
        <span class="cmp-sub" :class="{ warn: needsCheck || !!openGap }">{{
          needsCheck
            ? 'ใส่ตัวเลขแทน [เช็คตัวเลข] ก่อนส่ง'
            : openGap
              ? `เติม ${openGap} ก่อนส่ง`
            : head !== 'canned' && st.edited
              ? 'แก้จากร่างแล้ว ตรวจก่อนส่ง'
              : 'แก้ไขได้ก่อนส่ง'
        }}</span>
        <span class="grow"></span>
        <span class="kbd-hint"
          ><kbd>Enter</kbd> ส่ง<span v-if="head !== 'canned' && head !== 'quick'" class="kbd-more"
            >· <kbd>Shift</kbd>+<kbd>Enter</kbd> ขึ้นบรรทัด</span
          ></span
        >
      </div>
      <textarea
        ref="box"
        v-model="text"
        class="cmp-text"
        :class="{ draft: isDraft, plain: !head && !band, tall: tallBox }"
        rows="1"
        :aria-label="
          isDraft
            ? head === 'menu'
              ? 'ข้อความตอบกลับ รายชื่อเมนูรอบนี้ แก้ไขได้'
              : 'ข้อความตอบกลับ ร่างจาก AI แก้ไขได้'
            : head === 'script'
              ? 'ข้อความตอบกลับ จากคำแนะนำ แก้ไขได้'
              : 'ข้อความตอบกลับ'
        "
        placeholder="พิมพ์ข้อความตอบลูกค้า… พิมพ์ / เพื่อเลือกคำตอบสำเร็จรูป"
        aria-autocomplete="list"
        :aria-expanded="!!token"
        @keydown="onKey"
        @input="syncPicker"
        @click="syncPicker"
        @paste="onPaste"
        @blur="closePicker"
      ></textarea>

      <div v-if="staged.length || uploads.length" class="stg" aria-label="รูปและวิดีโอที่จะส่ง">
        <div class="stg-row">
          <span v-for="s in staged" :key="s.key" class="stg-i" :class="{ bad: channelBlock(s.asset, channel) }">
            <MediaThumb :asset="s.asset" :size="56" />
            <button type="button" class="stg-x" :aria-label="`เอา ${s.asset.title || 'รูปนี้'} ออก`" :title="s.asset.title" @click="lib.unstage(convId, s.key)">
              <Icon name="x" />
            </button>
          </span>
          <span v-for="u in uploads" :key="u.id" class="stg-i up" :class="{ bad: u.status === 'error' }" :title="u.name">
            <MediaThumb :asset="{ kind: u.kind ?? 'image', title: u.name, url: '', preview_url: null, duration_ms: null }" :src="u.thumb" :size="56" />
            <span v-if="u.status !== 'error'" class="stg-p" aria-hidden="true"><span :style="{ width: pct(u.progress) }"></span></span>
            <span v-if="u.status !== 'error'" class="stg-pct mono">{{ u.status === 'preparing' ? '…' : pct(u.progress) }}</span>
            <button type="button" class="stg-x" :aria-label="`ปิด ${u.name}`" @click="lib.dismissUpload(u.id)"><Icon name="x" /></button>
          </span>
          <span class="stg-note">{{
            uploading ? 'กำลังอัปโหลด… ส่งได้เมื่อเสร็จ' : `ส่ง${staged.length > 1 ? `รูปและวิดีโอ ${staged.length} รายการ` : 'ไฟล์นี้'}ต่อจากข้อความ`
          }}</span>
        </div>
        <p v-for="u in uploadErrors" :key="u.id" class="stg-msg bad">{{ u.error }}</p>
        <p v-if="lineOver" class="stg-msg bad">LINE ส่งได้ครั้งละ 5 ข้อความ (ข้อความ 1 + รูปหรือวิดีโอ 4) เอาออกอีก {{ lineOver }} รายการ</p>
        <p v-for="b in blocked" :key="b.s.key" class="stg-msg bad">“{{ b.s.asset.title || 'ไฟล์นี้' }}” {{ b.why }}</p>
      </div>

      <div class="cmp-tools">
        <button class="icon-b" aria-label="แนบรูปหรือวิดีโอจากเครื่อง" title="แนบรูปหรือวิดีโอจากเครื่อง" @click="fileInput?.click()">
          <Icon name="paperclip" />
        </button>
        <input ref="fileInput" class="sr" type="file" :accept="ACCEPT" multiple tabindex="-1" aria-hidden="true" @change="onFiles" />
        <button
          class="icon-b"
          :class="{ on: drawer }"
          aria-label="คลังรูปและวิดีโอ"
          title="คลังรูปและวิดีโอ"
          :aria-expanded="drawer"
          @click="openDrawer"
        >
          <Icon name="image" />
        </button>
        <EmojiButton :target="box" />
        <template v-if="bandHas && !script.suggestOff">
          <span class="vsep"></span>
          <button
            type="button"
            class="sgb"
            :class="{ on: bandOn }"
            :aria-expanded="bandOn"
            :title="bandOn ? 'ซ่อนคำแนะนำ' : 'ดูคำแนะนำคำตอบ (กด Tab ที่ช่องพิมพ์ว่างเพื่อใส่ได้ทันที)'"
            @click="script.toggleBand(convId)"
          >
            <Icon name="sparkles" />แนะนำคำตอบ<span v-if="suggestWaiting && !bandOn" class="sgb-dot" aria-label="มีคำแนะนำ"></span>
          </button>
        </template>
        <span v-if="canned.length" class="vsep"></span>
        <button v-for="c in canned" :key="c.id" class="canned" :title="c.text" @click="pickCanned(c)">
          <Icon name="zap" />{{ c.label }}
        </button>
        <span class="grow"></span>
        <button class="send" :disabled="!canSend" @click="send"><span>ส่ง</span><Icon name="send" /></button>
      </div>
      <div v-if="dragOver" class="drop-hint" aria-hidden="true"><Icon name="image" />วางรูปหรือวิดีโอที่นี่เพื่อแนบ</div>
    </div>
  </div>
</template>

<style scoped>
.ai-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 6px 10px;
  border-radius: 8px;
  background: #f3faf6;
  border: 1px solid #d6ebe0;
  font-size: 12.5px;
  line-height: 19px;
  color: #24453a;
}
.ai-note b {
  font-weight: 600;
  color: var(--ac2);
}
.ai-note :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.composer {
  position: relative;
  flex: none;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid var(--line);
}
.cmp {
  position: relative;
  border: 1px solid #cfe3d8;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(23, 33, 28, 0.04);
}
.cmp:focus-within {
  border-color: #9fcdb8;
}
.cmp.drop {
  border-color: var(--ac);
  border-style: dashed;
}
.drop-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 12px;
  background: rgba(234, 245, 239, 0.94);
  color: var(--ac2);
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  pointer-events: none;
}
.cmp-head {
  container-type: inline-size;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  padding: 8px 12px 0;
  min-width: 0;
}
.ai-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border-radius: 6px;
  background: var(--acs);
  color: var(--ac2);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.ai-chip.quick {
  background: #eef2f0;
  color: var(--ink);
}
.ai-chip.quick .mono {
  margin-left: 2px;
  color: var(--ac2);
}
.ai-chip :deep(.ic) {
  width: 14px;
  height: 14px;
}
.ai-chip.quick :deep(.ic) {
  color: var(--ac);
}
.cmp-sub.warn {
  color: #a4540b;
  font-weight: 600;
}
.cmp-sub,
.kbd-hint {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
}
.kbd-hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.kbd-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
/* a narrow middle column (1280 px screens): the head stays one line — "Enter ส่ง" only */
@container (max-width: 480px) {
  .kbd-more {
    display: none;
  }
}
kbd {
  font: 600 12px/18px var(--mono);
  padding: 0 6px;
  border-radius: 5px;
  background: #f4f6f5;
  border: 1px solid #d3ddd8;
  border-bottom-width: 2px;
  color: var(--ink);
}
.cmp-text {
  display: block;
  width: calc(100% - 24px);
  margin: 8px 12px 0;
  padding: 8px 12px;
  border: 0;
  border-left: 3px solid transparent;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 22px;
  color: var(--ink);
  outline: none;
  resize: none;
  min-height: 40px;
  max-height: 180px;
  overflow-y: auto;
}
.cmp-text.tall {
  max-height: max(180px, min(360px, 40vh));
}
.cmp-text.plain {
  margin-top: 4px;
}
.cmp-text.draft {
  background: #f5faf7;
  border-left-color: #7fbfa2;
  color: #33423b;
}
.cmp-text::placeholder {
  color: #7a8a82;
}
.stg {
  margin: 8px 12px 0;
}
.stg-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.stg-i {
  position: relative;
  display: inline-flex;
  border-radius: 10px;
  border: 1px solid var(--line);
  padding: 2px;
  background: #fff;
}
.stg-i.bad {
  border-color: #f2c7c2;
  box-shadow: 0 0 0 2px #fdecea;
}
.stg-x {
  position: absolute;
  top: -7px;
  right: -7px;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #17211c;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.stg-x :deep(.ic) {
  width: 12px;
  height: 12px;
  stroke-width: 2.4;
}
.stg-p {
  position: absolute;
  left: 6px;
  right: 6px;
  bottom: 6px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  overflow: hidden;
}
.stg-p span {
  display: block;
  height: 100%;
  background: var(--ac);
  transition: width 0.2s;
}
.stg-pct {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
  padding: 0 5px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  line-height: 18px;
  color: var(--ink);
}
.stg-note {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.stg-msg {
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.stg-msg.bad {
  color: var(--crit);
  font-weight: 500;
}
.cmp-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
}
.icon-b {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  flex: none;
  color: var(--mut);
}
.icon-b:hover,
.icon-b.on {
  background: #eef2f0;
  color: var(--ink);
}
.vsep {
  display: inline-block;
  width: 1px;
  height: 20px;
  background: var(--line);
  margin: 0 4px;
}
.canned {
  height: 30px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  background: #fff;
  color: #2e3b35;
}
.canned:hover {
  background: #f7f9f8;
}
/* "แนะนำคำตอบ": the band toggle — looks like the quick-answer chips, never counted as one */
.sgb {
  height: 30px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  border: 1px solid var(--line);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  background: #fff;
  color: #2e3b35;
}
.sgb:hover {
  background: #f7f9f8;
}
.sgb :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.sgb.on {
  border-color: var(--ac);
  background: #eef6f2;
  color: var(--ac);
}
.sgb-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ac);
  margin-left: 2px;
}
.canned :deep(.ic) {
  width: 14px;
  height: 14px;
  color: var(--ac);
}
.send {
  height: 36px;
  padding: 0 14px 0 16px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  background: var(--ac);
  color: #fff;
}
.send:hover:not(:disabled) {
  background: var(--ac2);
}
.send:disabled {
  background: #a9c9bb;
}
.send :deep(.ic) {
  width: 16px;
  height: 16px;
}
</style>
